var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
export class LegacyWebSocketSink {
    constructor(opts) {
        this.opts = opts;
        this.queue = [];
        this.chain = Promise.resolve();
        this._bytes = 0;
    }
    get bytesAccepted() {
        return this._bytes;
    }
    get queuedCount() {
        return this.queue.length;
    }
    /**
     * Take one chunk from the recorder.
     *
     * Serialised on a promise chain so chunks reach the socket in the order the
     * encoder produced them even when a send is slow.
     */
    accept(blob) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!blob || blob.size === 0)
                return;
            this._bytes += blob.size;
            this.chain = this.chain.then(() => this.dispatch(blob)).catch(() => undefined);
            return this.chain;
        });
    }
    dispatch(blob) {
        return __awaiter(this, void 0, void 0, function* () {
            // A backlog means the socket was down: this chunk goes behind the ones
            // already waiting, never in front of them.
            if (this.queue.length > 0) {
                this.enqueue(blob);
                yield this.drain();
                return;
            }
            const sent = yield this.opts.send(blob);
            if (!sent)
                this.enqueue(blob);
        });
    }
    enqueue(blob) {
        this.queue.push(blob);
        const max = this.opts.maxQueued;
        if (typeof max === 'number' && this.queue.length > max) {
            // Oldest out. A hole either way once the cap is reached; dropping the
            // oldest at least keeps the most recent context.
            this.queue.shift();
        }
    }
    /**
     * Drain buffered chunks in FIFO order. Called on reconnect by the context.
     *
     * Stops at the first failure and puts the remainder back at the FRONT, so a
     * socket that drops mid-drain does not scramble the order.
     */
    drain() {
        return __awaiter(this, void 0, void 0, function* () {
            var _a, _b;
            if (this.queue.length === 0)
                return;
            const queued = this.queue.splice(0);
            (_b = (_a = this.opts).onDrain) === null || _b === void 0 ? void 0 : _b.call(_a, queued.length);
            for (let i = 0; i < queued.length; i++) {
                // Sequential on purpose: cluster order.
                // eslint-disable-next-line no-await-in-loop
                const sent = yield this.opts.send(queued[i]);
                if (!sent) {
                    this.queue.unshift(...queued.slice(i));
                    return;
                }
            }
        });
    }
    /**
     * Hand the buffered chunks to the caller and clear the queue.
     *
     * The unload path needs this: on tab close the socket is torn down
     * synchronously, so whatever is still queued has to go out via `sendBeacon`
     * instead. Taking them (rather than copying) prevents the same bytes being
     * both beaconed and later re-sent.
     */
    takeQueued() {
        return this.queue.splice(0);
    }
    /** Wait for everything accepted so far to have been dispatched. The context
     *  still owns the `interview_video_complete` handshake. */
    finalize() {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.chain;
        });
    }
    abort(_reason) {
        return __awaiter(this, void 0, void 0, function* () {
            this.queue = [];
        });
    }
}
